import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { HsaQuestionType } from "./hsa-question-type";

// Condition leaves branch a flow on a previously-answered question. Each
// leaf references a pool question by `questionHandle` and applies an
// operator appropriate to that question's answer kind.
//
// Question types collapse to four leaf kinds based on shared answer shape:
//
//   Question types                                         Leaf kind
//   ────────────────────────────────────────────────────  ─────────────
//   AGE_INPUT_QUESTION                                    "numeric"
//   EMAIL_INPUT_QUESTION, TEXT_INPUT_QUESTION             "text"
//   SINGLE_SELECT_RADIO_QUESTION,                         "single_select"
//     SINGLE_SELECT_DROPDOWN_QUESTION,
//     SINGLE_SELECT_OTHER_QUESTION
//   MULTI_SELECT_QUESTION, MULTI_SELECT_NONE_QUESTION     "multi_select"
//
// The flow validator (hsa-flow-validate.ts) verifies that a leaf's
// `leafKind` matches the answer kind of the pool question it references.
// The leaf does not duplicate the question type — single source of truth
// is the pool.

export enum HsaConditionLeafKind {
  NUMERIC = "numeric",
  TEXT = "text",
  DATE = "date",
  SINGLE_SELECT = "single_select",
  MULTI_SELECT = "multi_select",
}

export enum HsaNumericConditionOperator {
  EQUALS = "equals",
  NOT_EQUALS = "not_equals",
  LESS_THAN = "less_than",
  LESS_THAN_OR_EQUAL = "less_than_or_equal",
  GREATER_THAN = "greater_than",
  GREATER_THAN_OR_EQUAL = "greater_than_or_equal",
}

export enum HsaTextConditionOperator {
  EQUALS = "equals",
  NOT_EQUALS = "not_equals",
  CONTAINS = "contains",
  NOT_CONTAINS = "not_contains",
  MATCHES_REGEX = "matches_regex",
  IS_EMPTY = "is_empty",
  IS_NOT_EMPTY = "is_not_empty",
}

// DATE leaves derive the answer's age in whole years from its ISO date value,
// compared against the current date at evaluation time (server-driven).
export enum HsaDateConditionOperator {
  AGE_LESS_THAN = "age_less_than",
  AGE_AT_LEAST = "age_at_least",
}

export enum HsaSingleSelectConditionOperator {
  IS_ANY_OF = "is_any_of",
  IS_NONE_OF = "is_none_of",
}

export enum HsaMultiSelectConditionOperator {
  INCLUDES_ANY = "includes_any",
  INCLUDES_ALL = "includes_all",
  INCLUDES_NONE = "includes_none",
  IS_EXACTLY = "is_exactly",
  IS_EMPTY = "is_empty",
  IS_NOT_EMPTY = "is_not_empty",
}

const hsaConditionLeafBase_ = z.object({ questionHandle: z.string().min(1) });

const hsaNumericConditionLeaf_ = hsaConditionLeafBase_.extend({
  leafKind: z.literal(HsaConditionLeafKind.NUMERIC),
  operator: createEnum(HsaNumericConditionOperator),
  value: z.number(),
});
export const hsaNumericConditionLeaf: z.ZodType<HsaNumericConditionLeaf> =
  hsaNumericConditionLeaf_;
export interface HsaNumericConditionLeaf extends z.infer<
  typeof hsaNumericConditionLeaf_
> {}

// `value` is required for every operator except IS_EMPTY / IS_NOT_EMPTY.
// The cross-field check lives in the flow validator so the leaf schema
// stays a plain object (cheap to compose into discriminated unions).
const hsaTextConditionLeaf_ = hsaConditionLeafBase_.extend({
  leafKind: z.literal(HsaConditionLeafKind.TEXT),
  operator: createEnum(HsaTextConditionOperator),
  value: z.string().optional(),
});
export const hsaTextConditionLeaf: z.ZodType<HsaTextConditionLeaf> =
  hsaTextConditionLeaf_;
export interface HsaTextConditionLeaf extends z.infer<
  typeof hsaTextConditionLeaf_
> {}

// `value` is the age threshold in whole years, required for both operators.
const hsaDateConditionLeaf_ = hsaConditionLeafBase_.extend({
  leafKind: z.literal(HsaConditionLeafKind.DATE),
  operator: createEnum(HsaDateConditionOperator),
  value: z.number().int().nonnegative(),
});
export const hsaDateConditionLeaf: z.ZodType<HsaDateConditionLeaf> =
  hsaDateConditionLeaf_;
export interface HsaDateConditionLeaf extends z.infer<
  typeof hsaDateConditionLeaf_
> {}

const hsaSingleSelectConditionLeaf_ = hsaConditionLeafBase_.extend({
  leafKind: z.literal(HsaConditionLeafKind.SINGLE_SELECT),
  operator: createEnum(HsaSingleSelectConditionOperator),
  optionValues: z.array(z.string().min(1)).min(1),
});
export const hsaSingleSelectConditionLeaf: z.ZodType<HsaSingleSelectConditionLeaf> =
  hsaSingleSelectConditionLeaf_;
export interface HsaSingleSelectConditionLeaf extends z.infer<
  typeof hsaSingleSelectConditionLeaf_
> {}

// `optionValues` is required for every operator except IS_EMPTY / IS_NOT_EMPTY.
// The cross-field check lives in the flow validator, mirroring the text leaf.
const hsaMultiSelectConditionLeaf_ = hsaConditionLeafBase_.extend({
  leafKind: z.literal(HsaConditionLeafKind.MULTI_SELECT),
  operator: createEnum(HsaMultiSelectConditionOperator),
  optionValues: z.array(z.string().min(1)).min(1).optional(),
});
export const hsaMultiSelectConditionLeaf: z.ZodType<HsaMultiSelectConditionLeaf> =
  hsaMultiSelectConditionLeaf_;
export interface HsaMultiSelectConditionLeaf extends z.infer<
  typeof hsaMultiSelectConditionLeaf_
> {}

const hsaConditionLeaf_ = z.discriminatedUnion("leafKind", [
  hsaNumericConditionLeaf_,
  hsaTextConditionLeaf_,
  hsaDateConditionLeaf_,
  hsaSingleSelectConditionLeaf_,
  hsaMultiSelectConditionLeaf_,
]);
export const hsaConditionLeaf: z.ZodType<HsaConditionLeaf> = hsaConditionLeaf_;
export type HsaConditionLeaf = z.infer<typeof hsaConditionLeaf_>;

const hsaConditionBlock_ = z.object({
  conjunction: createEnum(ConjunctionMode),
  conditions: z.array(hsaConditionLeaf_),
});
export const hsaConditionBlock: z.ZodType<HsaConditionBlock> =
  hsaConditionBlock_;
export interface HsaConditionBlock extends z.infer<typeof hsaConditionBlock_> {}

// Two fixed levels of nesting (blocks of leaves wrapped in an outer
// conjunction of blocks). Sufficient for any boolean formula via DNF/CNF
// and matches the PPU segment shape.
const hsaConditionExpression_ = z.object({
  conjunction: createEnum(ConjunctionMode),
  conditionBlocks: z.array(hsaConditionBlock_),
});
export const hsaConditionExpression: z.ZodType<HsaConditionExpression> =
  hsaConditionExpression_;
export interface HsaConditionExpression extends z.infer<
  typeof hsaConditionExpression_
> {}

// Many-to-one map from question type to leaf kind. Multiple question types
// share a leaf kind because the operator set depends on the answer's
// underlying shape, not the question's UI presentation.
export function hsaLeafKindForQuestionType(
  type: HsaQuestionType,
): HsaConditionLeafKind {
  switch (type) {
    case HsaQuestionType.AGE_INPUT_QUESTION:
      return HsaConditionLeafKind.NUMERIC;
    case HsaQuestionType.DATE_INPUT_QUESTION:
      return HsaConditionLeafKind.DATE;
    case HsaQuestionType.EMAIL_INPUT_QUESTION:
    case HsaQuestionType.TEXT_INPUT_QUESTION:
    case HsaQuestionType.PHONE_INPUT_QUESTION:
      return HsaConditionLeafKind.TEXT;
    case HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION:
    case HsaQuestionType.SINGLE_SELECT_DROPDOWN_QUESTION:
    case HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION:
      return HsaConditionLeafKind.SINGLE_SELECT;
    case HsaQuestionType.MULTI_SELECT_QUESTION:
    case HsaQuestionType.MULTI_SELECT_NONE_QUESTION:
    case HsaQuestionType.DYNAMIC_MULTI_SELECT_QUESTION:
      return HsaConditionLeafKind.MULTI_SELECT;
    default:
      assertNever(type);
  }
}
